import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../services/api';

const Container = styled.div`
  padding: 0;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 0;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  color: var(--gray-900);
  margin: 0;
  font-weight: bold;
`;

const Subtitle = styled.p`
  color: var(--gray-600);
  font-size: 0.875rem;
  margin: 0;
`;

const Form = styled.form`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  font-family: inherit;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const HelpText = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${props => props.variant === 'secondary' ? 'var(--bg-primary)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: ${props => props.variant === 'secondary' ? '1px solid var(--border-color)' : 'none'};

  &:hover {
    background: ${props => props.variant === 'secondary' ? 'var(--bg-secondary)' : 'var(--primary-hover)'};
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--text-secondary);
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const AIActionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    action_type: 'PROMPT',
    target_entity: 'Project',
    target_form: 'ProjectForm',
    prompt_template: '',
    n8n_webhook_url: '',
    input_fields: '[]',
    output_fields: '[]',
    is_active: true
  });

  useEffect(() => {
    if (id) {
      fetchAction();
    }
  }, [id]);

  const fetchAction = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/ai-actions/${id}`);
      const action = response.data;
      setFormData({
        name: action.name,
        description: action.description || '',
        action_type: action.action_type,
        target_entity: action.target_entity,
        target_form: action.target_form,
        prompt_template: action.prompt_template || '',
        n8n_webhook_url: action.n8n_webhook_url || '',
        input_fields: JSON.stringify(action.input_fields || [], null, 2),
        output_fields: JSON.stringify(action.output_fields || [], null, 2),
        is_active: action.is_active
      });
    } catch (error) {
      console.error('Error fetching action:', error);
      setError('Failed to load action');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      setLoading(true);
      
      // Parse JSON fields
      let inputFields, outputFields;
      try {
        inputFields = JSON.parse(formData.input_fields);
        outputFields = JSON.parse(formData.output_fields);
      } catch (err) {
        setError('Invalid JSON in input_fields or output_fields');
        setLoading(false);
        return;
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        action_type: formData.action_type,
        target_entity: formData.target_entity,
        target_form: formData.target_form,
        prompt_template: formData.prompt_template,
        n8n_webhook_url: formData.n8n_webhook_url,
        input_fields: inputFields,
        output_fields: outputFields,
        is_active: formData.is_active
      };

      if (id) {
        await api.put(`/ai-actions/${id}`, payload);
      } else {
        await api.post('/ai-actions', payload);
      }

      navigate('/ai-actions');
    } catch (error) {
      console.error('Error saving action:', error);
      setError(error.response?.data?.error || 'Failed to save action');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/ai-actions');
  };

  if (loading && id) {
    return (
      <Container>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div className="spinner"></div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate('/ai-actions')}
          className="breadcrumb-link"
        >
          AI Actions
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {id ? 'Edit' : 'Create'}
        </span>
      </nav>

      <Form onSubmit={handleSubmit} className="card">
        {/* Header inside card */}
        <Header style={{
          paddingBottom: '1.5rem',
          marginBottom: '1.5rem',
          borderBottom: '3px solid #7C3AED'
        }}>
          <div>
            <Title style={{ marginBottom: '0.25rem' }}>
              {id ? '✏️ Edit AI Action' : '⚡ Create AI Action'}
            </Title>
            <Subtitle>
              {id ? 'Update your AI action configuration' : 'Define a reusable AI-powered transformation'}
            </Subtitle>
          </div>
        </Header>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <FormGroup>
          <Label>Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Rewrite Project Description"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe what this action does..."
          />
        </FormGroup>

        <FormGroup>
          <Label>Action Type *</Label>
          <Select
            value={formData.action_type}
            onChange={(e) => setFormData({ ...formData, action_type: e.target.value })}
            required
          >
            <option value="PROMPT">AI Prompt</option>
            <option value="N8N_WORKFLOW">n8n Workflow</option>
            <option value="CREWAI_PROBLEM">CrewAI Multi-Agent</option>
          </Select>
          <HelpText>
            {formData.action_type === 'PROMPT' && 'Use AI to generate content based on a prompt template'}
            {formData.action_type === 'N8N_WORKFLOW' && 'Trigger an n8n workflow via webhook'}
            {formData.action_type === 'CREWAI_PROBLEM' && 'Use CrewAI multi-agent system for complex tasks'}
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Target Entity *</Label>
          <Input
            value={formData.target_entity}
            onChange={(e) => setFormData({ ...formData, target_entity: e.target.value })}
            placeholder="e.g., Project, Campaign, Customer"
            required
          />
          <HelpText>The entity type this action applies to</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Target Form *</Label>
          <Input
            value={formData.target_form}
            onChange={(e) => setFormData({ ...formData, target_form: e.target.value })}
            placeholder="e.g., ProjectForm, CampaignForm"
            required
          />
          <HelpText>The form where this action will appear</HelpText>
        </FormGroup>

        {(formData.action_type === 'PROMPT' || formData.action_type === 'prompt') && (
          <FormGroup>
            <Label>Prompt Template</Label>
            <TextArea
              value={formData.prompt_template}
              onChange={(e) => setFormData({ ...formData, prompt_template: e.target.value })}
              placeholder="Use {field_name} for placeholders. Example: Rewrite this description to be more compelling: {description}"
              style={{ minHeight: '150px' }}
            />
            <HelpText>
              Use curly braces for field placeholders: {'{field_name}'}
            </HelpText>
          </FormGroup>
        )}

        {(formData.action_type === 'N8N_WORKFLOW' || formData.action_type === 'n8n_workflow') && (
          <FormGroup>
            <Label>n8n Webhook URL</Label>
            <Input
              value={formData.n8n_webhook_url}
              onChange={(e) => setFormData({ ...formData, n8n_webhook_url: e.target.value })}
              placeholder="https://your-n8n.com/webhook/..."
            />
            <HelpText>The webhook URL of your n8n workflow</HelpText>
          </FormGroup>
        )}

        <FormGroup>
          <Label>Input Fields (JSON Array)</Label>
          <TextArea
            value={formData.input_fields}
            onChange={(e) => setFormData({ ...formData, input_fields: e.target.value })}
            placeholder='["name", "description", "budget"]'
            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
          />
          <HelpText>
            Example: ["name", "description", "target_audience", "budget"]
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Output Fields (JSON Array)</Label>
          <TextArea
            value={formData.output_fields}
            onChange={(e) => setFormData({ ...formData, output_fields: e.target.value })}
            placeholder='["description", "tags"]'
            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
          />
          <HelpText>
            Example: ["description", "marketing_copy", "seo_keywords", "headline"]
          </HelpText>
        </FormGroup>

        <ButtonGroup>
          <Button type="submit" disabled={loading}>
            {loading ? 'Saving...' : id ? 'Update Action' : 'Create Action'}
          </Button>
          <Button type="button" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default AIActionForm;
